import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, PartyPopper, AlertCircle } from 'lucide-react';
import Layout from '../../components/Layout';
import ReportPane from './ReportPane';
import VerdictBar from './VerdictBar';
import { usePairwiseAutosave } from './usePairwiseAutosave';
import { MIN_REMARKS_LENGTH, RATING_CONTENT_MAX_WIDTH, VERDICT_OPTIONS } from './constants';
import {
  fetchNextPair,
  fetchPairProgress,
  fetchPairs,
  skipPair,
  type PairProgress,
  type PairSummary,
  type Verdict,
} from './api';

/**
 * RLHF pairwise comparison workbench.
 *
 * A rater reads two simulation reports side by side and records which one is better, with
 * written reasoning. The choice is forced — there is no tie. Entirely independent of the 1-5 scoring page: separate
 * queue, separate endpoints, separate dataset. A rater may work both, and a verdict here has no
 * bearing on any score there.
 *
 * Navigation mirrors the 1-5 track deliberately — same queue semantics, same autosave rules —
 * because raters move between the two and inconsistent behaviour between them is its own bug.
 */

const ComparisonWorkbench: React.FC = () => {
  const [queue, setQueue] = useState<PairSummary[]>([]);
  const [index, setIndex] = useState(0);
  const [progress, setProgress] = useState<PairProgress | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [exhausted, setExhausted] = useState(false);
  const [fatalError, setFatalError] = useState<string | null>(null);
  // Recoverable problems — shown inline, never replacing the page or the rater's work.
  const [navError, setNavError] = useState<string | null>(null);

  const [verdict, setVerdict] = useState<Verdict | null>(null);
  const [remarks, setRemarks] = useState('');
  // Gates autosave while the form is populated from the server, so hydrating an existing verdict
  // doesn't immediately write it back.
  const [hydrated, setHydrated] = useState(false);

  const current = queue[index] ?? null;

  /**
   * Mirror every committed save into the queue, drafts included.
   *
   * The queue is what the hydration effect restores from when the rater navigates back, so if
   * drafts don't land here it goes stale: the server holds the reasoning while the queue still
   * holds null, revisiting shows an empty box, and the next keystroke writes that blank over
   * good text. Mirroring only on submit is not enough — most typing is persisted by drafts.
   */
  const mirrorToQueue = useCallback(
    (c: { itemId: number; choice: Verdict | null; remarks: string; draft: boolean }) => {
      setQueue((prev) =>
        prev.map((p) =>
          p.id === c.itemId
            ? {
                ...p,
                verdict: c.choice,
                remarks: c.remarks,
                is_judged: c.draft ? p.is_judged : true,
                status: c.draft ? p.status : 'submitted',
              }
            : p,
        ),
      );
    },
    [],
  );

  const {
    saveState,
    error: saveError,
    flush,
    setBaseline,
    cancelPending,
  } = usePairwiseAutosave({
    pairId: current?.id ?? null,
    verdict,
    remarks,
    enabled: hydrated,
    onSaved: mirrorToQueue,
  });

  const refreshProgress = useCallback(async () => {
    try {
      setProgress(await fetchPairProgress());
    } catch {
      /* progress is decorative; never block judging on it */
    }
  }, []);

  /** Load the queue, claiming a first pair if the rater has none yet. */
  const bootstrap = useCallback(async () => {
    setLoading(true);
    try {
      let pairs = await fetchPairs();
      if (pairs.length === 0) {
        const claimed = await fetchNextPair();
        if (!claimed) {
          setExhausted(true);
          setLoading(false);
          return;
        }
        pairs = await fetchPairs();
      }
      setQueue(pairs);
      // Land on the first thing still needing judgment rather than the top of history.
      const firstUnjudged = pairs.findIndex((p) => !p.is_judged);
      setIndex(firstUnjudged === -1 ? pairs.length - 1 : firstUnjudged);
      void refreshProgress();
    } catch (e) {
      setFatalError(e instanceof Error ? e.message : 'Could not load your comparison queue');
    } finally {
      setLoading(false);
    }
  }, [refreshProgress]);

  useEffect(() => {
    void bootstrap();
  }, [bootstrap]);

  // Keyed on the pair IDENTITY only. Depending on current.verdict/current.remarks too would mean
  // that locally marking a pair judged re-runs this and resets the form from a stale queue entry
  // — the exact combination that silently destroyed remarks on the 1-5 track.
  useEffect(() => {
    // Narrowed at runtime, not just by the type. `verdict` comes from the server, and a row
    // stored before ties were dropped still carries 'tie' until the migration runs. Left as-is
    // it matches no option — nothing renders selected while Next, gated only on null, stays
    // enabled — so the rater submits it and gets a 422 they cannot act on.
    const stored = current?.verdict ?? null;
    const loadedVerdict = VERDICT_OPTIONS.some((o) => o.value === stored) ? stored : null;
    const loadedRemarks = current?.remarks ?? '';
    setHydrated(false);
    setVerdict(loadedVerdict);
    setRemarks(loadedRemarks);
    // Adopt the server values as the baseline, so merely opening a pair is never mistaken for an
    // edit and cannot trigger a write.
    setBaseline(current?.id ?? null, loadedVerdict, loadedRemarks);
    const id = setTimeout(() => setHydrated(true), 0);
    return () => clearTimeout(id);
  }, [current?.id, setBaseline]); // eslint-disable-line react-hooks/exhaustive-deps

  const goNext = useCallback(async () => {
    if (!current) return;
    setBusy(true);
    try {
      // Commit before moving. A failed save must block navigation — advancing would silently
      // discard the rater's reasoning.
      // On success `mirrorToQueue` has already written the verdict and reasoning into the
      // queue entry, so there is nothing further to apply locally.
      const ok = await flush(false);
      if (!ok) return;

      const nextUnjudged = queue.findIndex((p, i) => i > index && !p.is_judged);
      if (nextUnjudged !== -1) {
        setIndex(nextUnjudged);
      } else {
        const claimed = await fetchNextPair();
        if (!claimed) {
          setExhausted(true);
        } else {
          const pairs = await fetchPairs();
          setQueue(pairs);
          const landing = pairs.findIndex((p) => p.id === claimed.id);
          setIndex(landing === -1 ? pairs.length - 1 : landing);
        }
      }
      void refreshProgress();
    } catch (e) {
      // The verdict itself was saved above; only the advance failed.
      setNavError(e instanceof Error ? e.message : 'Saved, but could not load the next pair');
    } finally {
      setBusy(false);
    }
  }, [current, flush, queue, index, refreshProgress]);

  const goPrev = useCallback(async () => {
    if (index === 0) return;
    setBusy(true);
    try {
      // Same rule as Next: never navigate away from work we failed to persist.
      if (!(await flush(true))) return;
      setIndex((i) => Math.max(0, i - 1));
    } finally {
      setBusy(false);
    }
  }, [index, flush]);

  const goToIndex = useCallback(
    async (target: number) => {
      if (target === index) return;
      setBusy(true);
      try {
        if (!(await flush(true))) return;
        setIndex(target);
      } finally {
        setBusy(false);
      }
    },
    [index, flush],
  );

  const skip = useCallback(async () => {
    if (!current) return;
    // Skip is now terminal and REQUIRES a reason (captured in the remarks box). It is only offered
    // when no winner is chosen, so there is nothing to discard and no confirm. Disarm the pending
    // draft save so the debounce can't race the skip write.
    const reason = remarks.trim();
    if (reason.length < MIN_REMARKS_LENGTH) return;
    cancelPending();
    setBusy(true);
    try {
      // Terminal skip: the pair is closed out with its reason and its sims are never re-paired, so
      // — unlike the old release path — there is no need to exclude them from the next claim.
      // Advance like a normal submit: mark it handled, then move to the next unjudged pair or a
      // fresh claim.
      await skipPair(current.id, reason);
      const claimed = await fetchNextPair();
      const refreshed = await fetchPairs();
      setQueue(refreshed);

      if (refreshed.length === 0) {
        setExhausted(true);
        setIndex(0);
      } else if (claimed) {
        const landing = refreshed.findIndex((p) => p.id === claimed.id);
        setIndex(landing === -1 ? refreshed.length - 1 : landing);
      } else {
        // Nothing new to claim: land on the rater's own next unjudged pair, else the end.
        const pending = refreshed.findIndex((p) => !p.is_judged);
        setIndex(pending === -1 ? refreshed.length - 1 : pending);
        setExhausted(pending === -1);
      }
      void refreshProgress();
    } catch (e) {
      // Non-fatal: keep the rater on the page with their work intact.
      setNavError(e instanceof Error ? e.message : 'Could not skip this pair');
    } finally {
      setBusy(false);
    }
  }, [current, remarks, refreshProgress, cancelPending]);

  const judgedCount = useMemo(() => queue.filter((p) => p.is_judged).length, [queue]);
  const percent = queue.length ? Math.round((judgedCount / queue.length) * 100) : 0;

  if (loading) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center py-32 gap-3">
          <Loader2 className="w-7 h-7 animate-spin text-blue-600" aria-hidden="true" />
          <p className="text-gray-500 text-sm">Loading your comparison queue…</p>
        </div>
      </Layout>
    );
  }

  if (fatalError) {
    return (
      <Layout>
        <div className="max-w-md mx-auto text-center py-32">
          <AlertCircle className="w-9 h-9 text-red-500 mx-auto mb-3" aria-hidden="true" />
          <h1 className="text-lg font-semibold mb-1">Can’t start comparing</h1>
          <p className="text-gray-600 text-sm mb-5">{fatalError}</p>
          <button
            type="button"
            onClick={() => {
              setFatalError(null);
              void bootstrap();
            }}
            className="px-4 py-2 min-h-[44px] rounded-xl bg-gray-900 text-white text-sm font-semibold"
          >
            Try again
          </button>
        </div>
      </Layout>
    );
  }

  if (exhausted && !queue.some((p) => !p.is_judged)) {
    return (
      <Layout>
        <div className="max-w-md mx-auto text-center py-32">
          <PartyPopper className="w-9 h-9 text-blue-600 mx-auto mb-3" aria-hidden="true" />
          <h1 className="text-lg font-semibold mb-1">No more pairs to compare</h1>
          <p className="text-gray-600 text-sm">
            Every available report has been paired and judged
            {progress
              ? ` — ${progress.judged} comparisons in total${progress.skipped ? `, ${progress.skipped} skipped` : ''}.`
              : '.'}{' '}
            Thank you!
          </p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className={`${RATING_CONTENT_MAX_WIDTH} mx-auto pb-56`}>
        <div className="flex justify-between items-start gap-5 flex-wrap">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.09em] text-blue-700">
              RLHF · Which report is better?
            </p>
            <h1 className="text-[21px] font-semibold tracking-tight mt-1">
              Compare two simulation reports
            </h1>
            <p className="text-gray-500 text-[13px]">
              Read both, pick the stronger one, and say why · comparison {index + 1} of{' '}
              {queue.length}
            </p>
          </div>

          <div className="bg-white border border-gray-200 rounded-xl px-4 py-3 min-w-[210px]">
            <div className="flex justify-between items-baseline">
              <p className="text-[17px] font-semibold">
                {judgedCount}{' '}
                <span className="text-gray-500 text-xs font-normal">of {queue.length} judged</span>
              </p>
              <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">
                {percent}%
              </span>
            </div>
            <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden mt-2">
              <div className="h-full bg-blue-500 transition-all" style={{ width: `${percent}%` }} />
            </div>
            {progress && (
              <p className="text-[11px] text-gray-500 mt-1.5">
                {progress.available_pairs} more pairs available
              </p>
            )}
          </div>
        </div>

        {navError && (
          <div
            role="alert"
            className="mt-3 flex items-start gap-2 rounded-xl border border-amber-300
                          bg-amber-50 px-3 py-2 text-[13px] text-amber-900"
          >
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" aria-hidden="true" />
            <span className="flex-1">{navError}</span>
            <button
              type="button"
              onClick={() => setNavError(null)}
              className="font-semibold underline shrink-0"
            >
              Dismiss
            </button>
          </div>
        )}

        <div className="flex gap-1.5 items-center mt-4 mb-1 overflow-x-auto pb-1.5">
          <span className="text-[11px] text-gray-500 mr-1 shrink-0">Your queue</span>
          {queue.map((p, i) => (
            <button
              key={p.id}
              type="button"
              onClick={() => void goToIndex(i)}
              disabled={busy}
              aria-label={`Comparison ${i + 1}${p.is_judged ? ', judged' : ', not yet judged'}`}
              aria-current={i === index}
              className={`w-7 h-7 shrink-0 rounded-lg text-[11px] font-semibold border
                          transition-colors disabled:opacity-50 ${
                            i === index
                              ? 'bg-gray-900 border-gray-900 text-white'
                              : p.is_judged
                                ? 'bg-blue-50 border-blue-200 text-blue-700'
                                : 'bg-white border-gray-200 text-gray-500'
                          }`}
            >
              {i + 1}
            </button>
          ))}
        </div>

        {current && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3.5 mt-3 items-start">
            <ReportPane
              pairId={current.id}
              side="a"
              itemRef={current.sim_a_id}
              title={current.sim_a_name}
              selected={verdict === 'a'}
              disabled={busy}
              onPick={() => setVerdict('a')}
            />
            <ReportPane
              pairId={current.id}
              side="b"
              itemRef={current.sim_b_id}
              title={current.sim_b_name}
              selected={verdict === 'b'}
              disabled={busy}
              onPick={() => setVerdict('b')}
            />
          </div>
        )}
      </div>

      <VerdictBar
        verdict={verdict}
        remarks={remarks}
        onVerdictChange={setVerdict}
        onRemarksChange={setRemarks}
        onPrev={() => void goPrev()}
        onNext={() => void goNext()}
        onSkip={() => void skip()}
        canGoPrev={index > 0}
        isLast={index === queue.length - 1}
        busy={busy}
        saveState={saveState}
        error={saveError}
      />
    </Layout>
  );
};

export default ComparisonWorkbench;
